import { BigDecimal } from "effect"
import { formatMoney, formatPercentOf, formatPrice, formatQty, formatSignedMoney } from "../trading/format.ts"
import type { BoxLabels } from "./tradeBoxPrimitive.ts"

// The texts TradingView puts on its long / short position tool (docs/design/0016): for the target
// and the stop, their distance from the entry (price, percent, ticks) and the amount made or lost
// there; at the entry, the trade's P&L (open or closed), its quantity and the risk / reward ratio.

type Decimal = BigDecimal.BigDecimal

export interface BoxLabelInput {
  readonly entry: number
  readonly stopLoss: number | null
  readonly takeProfit: number | null
  readonly qty: Decimal
  readonly pnl: Decimal
  readonly pnlName: "Open P&L" | "Closed P&L"
}

/** "1.9": the target's distance over the stop's, from the entry; null without both. */
export const riskRewardRatio = (entry: number, stopLoss: number | null, takeProfit: number | null): string | null => {
  if (stopLoss === null || takeProfit === null) return null
  const risk = Math.abs(entry - stopLoss)
  if (risk === 0) return null
  return String(Number((Math.abs(takeProfit - entry) / risk).toFixed(2)))
}

/** `tickSize` null (a backtest, which has no instrument): no tick counts, prices to 2 decimals. */
export const boxLabels = (input: BoxLabelInput, tickSize: Decimal | null, currency: string): BoxLabels => {
  const precision = tickSize === null ? 2 : Math.max(0, BigDecimal.normalize(tickSize).scale)
  const tick = tickSize === null ? 0 : BigDecimal.toNumberUnsafe(tickSize)
  const entry = BigDecimal.fromNumberUnsafe(input.entry)
  const qty = Math.abs(BigDecimal.toNumberUnsafe(input.qty))
  const leg = (name: string, price: number | null): string | null => {
    if (price === null) return null
    const distance = Math.abs(price - input.entry)
    const percent = formatPercentOf(BigDecimal.fromNumberUnsafe(distance), entry).replace(/^\+/, "")
    const ticks = tick > 0 ? ` ${Math.round(distance / tick).toLocaleString("en-US")} ticks` : ""
    const amount = formatMoney(BigDecimal.fromNumberUnsafe(Number((distance * qty).toFixed(8))), currency)
    return `${name}: ${formatPrice(BigDecimal.fromNumberUnsafe(distance), precision, currency)} (${percent})${ticks}, Amount: ${amount}`
  }
  const ratio = riskRewardRatio(input.entry, input.stopLoss, input.takeProfit)
  return {
    reward: leg("Target", input.takeProfit),
    risk: leg("Stop", input.stopLoss),
    entry: [
      `${input.pnlName}: ${formatSignedMoney(input.pnl, currency)}, Qty: ${formatQty(BigDecimal.abs(input.qty), currency)}`,
      ...(ratio === null ? [] : [`Risk/reward ratio: ${ratio}`])
    ],
    entryWinning: !BigDecimal.isNegative(input.pnl)
  }
}
